
import { TrainingSessionForm } from "@/components/training-sessions/TrainingSessionForm";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useAuth } from "@/components/AuthProvider";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";
import { useEffect } from "react";

const NewStudentTrainingSession = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("student-sessions");
  const queryClient = useQueryClient();
  
  // Pré-invalidar as queries para garantir dados frescos ao voltar
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ["student-training-sessions"] });
    };
  }, [queryClient]);

  // Buscar o perfil do aluno
  const { data: studentProfile, isLoading } = useQuery({
    queryKey: ["student-profile", user?.email],
    queryFn: async () => {
      if (!user?.email) return null;

      const { data, error } = await supabase
        .from("students")
        .select("*")
        .eq("email", user.email)
        .eq("status", "active")
        .maybeSingle();

      if (error) {
        console.error("Error fetching student profile:", error);
        return null;
      }

      return data;
    },
    enabled: !!user?.email
  });

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700/50">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight gradient-text">{t("form.title")}</h2>
            <p className="text-muted-foreground dark:text-gray-400">
              {t("subtitle")}
            </p>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href={getPath("/student-dashboard")} className="text-flame hover:text-flame/80">{t("breadcrumbs.dashboard")}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href={getPath("/student-dashboard/training-sessions")} className="text-flame hover:text-flame/80">{t("title")}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-medium dark:text-white">{t("form.title")}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>
        <div className="dark:text-white">A carregar...</div>
      </div>
    );
  }

  if (!studentProfile) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700/50">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight gradient-text">{t("form.title")}</h2>
            <p className="text-muted-foreground dark:text-gray-400">
              {t("subtitle")}
            </p>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href={getPath("/student-dashboard")} className="text-flame hover:text-flame/80">{t("breadcrumbs.dashboard")}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href={getPath("/student-dashboard/training-sessions")} className="text-flame hover:text-flame/80">{t("title")}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-medium dark:text-white">{t("form.title")}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>

        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-white">Perfil não encontrado</CardTitle>
            <CardDescription className="dark:text-gray-300">
              Não foi possível encontrar seu perfil de aluno. Por favor, contacte seu personal trainer.
            </CardDescription>
          </CardHeader>
        </Card>

        <Button onClick={() => navigate(getPath("/student-dashboard"))}>
          Voltar para Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700/50">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight gradient-text">{t("form.title")}</h2>
          <p className="text-muted-foreground dark:text-gray-400">
            {t("subtitle")}
          </p>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/student-dashboard")} className="text-flame hover:text-flame/80">{t("breadcrumbs.dashboard")}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/student-dashboard/training-sessions")} className="text-flame hover:text-flame/80">{t("title")}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium dark:text-white">{t("breadcrumbs.newSession")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <TrainingSessionForm studentProfile={studentProfile} />
    </div>
  );
};

export default NewStudentTrainingSession;
